import { useDispatch } from 'react-redux'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  IconButtonProps,
  Tooltip
} from '@mui/material'
import { Edit as EditIcon } from '@mui/icons-material'
import { hideLift, showLift } from '../redux/slices/lifts'
import { useTranslation } from 'react-i18next'
import { Project, Lift } from '../types'
import { useState } from 'react'
import LiftForm from './LiftForm'
import { usePolicy } from '../hooks'
import { ProjectStateType } from '../enums/ProjectStateType'

export interface LiftEditButtonProps extends Omit<IconButtonProps, 'onClick' | 'children'> {
  project?: Project
  lift: Lift
}

export default function LiftEditButton({ project, lift, ...props }: LiftEditButtonProps) {
  /**
   * The translate function.
   */
  const [t] = useTranslation('common')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The policies.
   */
  const { canEditProject } = usePolicy()

  /**
   * Indicates if the dialog is open.
   */
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)

  /**
   * Open the dialog.
   */
  function openDialog(): void {
    dispatch(showLift(lift))
    setIsDialogOpen(true)
  }

  /**
   * Close the dialog.
   */
  function closeDialog(): void {
    setIsDialogOpen(false)
    dispatch(hideLift())
  }

  return (
    <>
      {project && project.state !== ProjectStateType.ARCHIVED && canEditProject(project) && (
        <Tooltip title={t('edit')}>
          <IconButton size="small" data-testid="edit" {...props} onClick={() => openDialog()}>
            <EditIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      )}

      <Dialog open={isDialogOpen} maxWidth="sm" fullWidth onClose={() => closeDialog()}>
        <DialogTitle>{t('editLift')}</DialogTitle>
        <DialogContent>
          <LiftForm
            value={{ project: project?.id }}
            onCancel={() => closeDialog()}
            afterSubmit={() => closeDialog()}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
